import { ReactFormExtendedApi, useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Modal,
  PhoneNumberInput,
  TextInput
} from '@thedealersconcierge/components';
import { getFormFieldErrorMsg } from '@thedealersconcierge/lib/utils/forms';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import Header from '~/components/Header';
import { gqlMutationClient, gqlQueryClient } from '~/lib/backend';
import meQuery from '~/query/meQuery';
import { useModals } from '~/router';
import { dealershipAtom } from '~/state';
import UserDropDownField from './_components/UserDropDownField';

type FormType = {
  // Needs to accept null so tanstack can properly clear it
  salesPersonId: string | null;
  salesManagerId: string | null;
  fniManagerId: string | null;
  bdcId: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

const Step1: FC<{
  /* eslint-disable @typescript-eslint/no-explicit-any */
  form: ReactFormExtendedApi<
    FormType,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  >;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  onNext: () => void;
}> = ({ form, onNext }) => {
  const dealership = useAtomValue(dealershipAtom);

  const dealershipId = dealership?.activeDealershipPerms.dealershipId;

  return (
    <div className="flex flex-col space-y-4">
      <div className="text-heading-2 mb-4">Step 1: Staff</div>
      <div className="flex flex-col gap-spacing-04">
        <form.Field name="salesPersonId">
          {(field) => {
            return (
              <UserDropDownField
                dealershipId={dealershipId}
                value={field.state.value}
                disabled={false}
                labeltext="Sales Person"
                handleChange={field.handleChange}
                roleToDisplay={'SALES_PERSON'}
                backgroundType="DARK"
              />
            );
          }}
        </form.Field>

        <form.Field name="salesManagerId">
          {(field) => {
            return (
              <UserDropDownField
                dealershipId={dealershipId}
                value={field.state.value}
                disabled={false}
                labeltext="Sales Manager"
                handleChange={field.handleChange}
                roleToDisplay={'SALES_MANAGER'}
                backgroundType="DARK"
              />
            );
          }}
        </form.Field>

        <form.Field name="fniManagerId">
          {(field) => {
            return (
              <UserDropDownField
                dealershipId={dealershipId}
                value={field.state.value}
                disabled={false}
                labeltext="F&I Manager"
                handleChange={field.handleChange}
                roleToDisplay={'FNI_MANAGER'}
                backgroundType="DARK"
              />
            );
          }}
        </form.Field>

        <form.Field name="bdcId">
          {(field) => {
            return (
              <UserDropDownField
                dealershipId={dealershipId}
                value={field.state.value}
                disabled={false}
                labeltext="BDC"
                handleChange={field.handleChange}
                roleToDisplay={'BDC'}
                backgroundType="DARK"
              />
            );
          }}
        </form.Field>
      </div>

      <div className="flex flex-row space-x-4 justify-end">
        <Button
          label="Next"
          dataTestId="create-transaction-next"
          onClick={onNext}
          type="button"
          size="MEDIUM"
        />
      </div>
    </div>
  );
};

const Step2: FC<{
  back: () => void;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  form: ReactFormExtendedApi<
    FormType,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  >;
  /* eslint-enable @typescript-eslint/no-explicit-any */
}> = ({ back, form }) => {
  return (
    <div className="flex flex-col space-y-4">
      <div className="text-heading-2 mb-4">Step 2: Customer</div>
      <div className="flex flex-col w-full space-y-spacing-04">
        <form.Field
          name="firstName"
          validators={{
            onChange: ({ value }) =>
              !value ? 'First name is required' : undefined
          }}
          children={(field) => {
            // Avoid hasty abstractions. Render props are great!
            return (
              <TextInput
                dataTestId="create-transaction-first-name"
                name={field.name}
                value={field.state.value ?? ''}
                label="First Name"
                placeholder="First Name"
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errorMessage={getFormFieldErrorMsg(field)}
                required={true}
                disabled={false}
                backgroundType="DARK"
                hideAssistiveMessage
              />
            );
          }}
        />

        <form.Field
          name="lastName"
          validators={{
            onChange: ({ value }) =>
              !value ? 'Last name is required' : undefined
          }}
          children={(field) => (
            <TextInput
              dataTestId="create-transaction-last-name"
              name={field.name}
              value={field.state.value ?? ''}
              label="Last Name"
              placeholder="Last Name"
              onChange={field.handleChange}
              onBlur={field.handleBlur}
              errorMessage={getFormFieldErrorMsg(field)}
              required={true}
              disabled={false}
              backgroundType="DARK"
              hideAssistiveMessage
            />
          )}
        />

        <form.Field
          name="email"
          validators={{
            onChange: ({ value }) => (!value ? 'Email is required' : undefined)
          }}
          children={(field) => {
            // Avoid hasty abstractions. Render props are great!
            return (
              <TextInput
                dataTestId="create-transaction-email"
                name={field.name}
                value={field.state.value ?? ''}
                label="Email"
                inputType="email"
                placeholder="Email"
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errorMessage={getFormFieldErrorMsg(field)}
                required={true}
                disabled={false}
                backgroundType="DARK"
                hideAssistiveMessage
              />
            );
          }}
        />

        <form.Field
          name="phoneNumber"
          validators={{
            onChange: ({ value }) =>
              !value ? 'Phone Number is required' : undefined
          }}
          children={(field) => (
            <PhoneNumberInput
              dataTestId="create-transaction-phone-number"
              name={field.name}
              value={field.state.value ?? ''}
              label="Phone Number"
              placeholder="Phone Number"
              onChange={field.handleChange}
              onBlur={field.handleBlur}
              errorMessage={getFormFieldErrorMsg(field)}
              required={true}
              disabled={false}
              backgroundType="DARK"
            />
          )}
        />
      </div>

      <form.Subscribe
        children={(form) => {
          return (
            <div className="flex flex-row space-x-4 justify-end w-full">
              <Button
                label="Back"
                variant="SECONDARY"
                onClick={() => {
                  back();
                }}
                disabled={form.isSubmitting}
                type="button"
                size="MEDIUM"
              />

              <Button
                label="Create New Transaction"
                dataTestId="create-transaction-submit"
                variant="PRIMARY"
                type="submit"
                isLoading={form.isSubmitting}
                disabled={
                  !form.canSubmit ||
                  form.isSubmitting ||
                  !form.values['email'] ||
                  !form.values['firstName'] ||
                  !form.values['lastName'] ||
                  !form.values['phoneNumber']
                }
                size="MEDIUM"
              />
            </div>
          );
        }}
      />
    </div>
  );
};

export default function CreatePage() {
  const { close } = useModals();
  const { data: meData } = useQuery(meQuery());
  const [step, setStep] = useState(1);
  const dealership = useAtomValue(dealershipAtom);

  const currentPerms = dealership?.activeDealershipPerms;
  const userId = meData?.me?.user?.id ?? '';

  const defaultValues: FormType = {
    salesPersonId:
      currentPerms?.role === 'SALES_PERSON'
        ? userId // If the logged in user is a sales person themselves, we select them
        : '',
    salesManagerId:
      currentPerms?.role === 'SALES_MANAGER'
        ? userId // If the logged in user is a sales manager themselves, we select them
        : '',
    fniManagerId:
      currentPerms?.role === 'FNI_MANAGER'
        ? userId // If the logged in user is a FNI manager themselves, we select them
        : '',
    bdcId:
      currentPerms?.role === 'BDC'
        ? userId // If the logged in user is a BDC themselves, we select them
        : '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  };
  const form = useForm({
    defaultValues,
    onSubmit: async ({ value: values }) => {
      if (!dealership?.activeDealershipPerms.dealershipId) {
        throw new Error('No dealership activte');
      }

      const transaction = await gqlMutationClient()({
        dealershipCreateTransaction: [
          {
            customer: {
              email: values.email,
              phoneNumber: values.phoneNumber,
              firstName: values.firstName,
              lastName: values.lastName
            },
            // On submit set to undefined
            staff: {
              bdcId: values.bdcId ?? undefined,
              fniManagerId: values.fniManagerId ?? undefined,
              salesManagerId: values.salesManagerId ?? undefined,
              salesPersonId: values.salesPersonId ?? undefined
            }
          },
          {
            __typename: true,
            '...on GraphQLError': {
              message: true
            },
            '...on MutationDealershipCreateTransactionSuccess': {
              data: {
                status: true,
                id: true
              }
            }
          }
        ]
      });
      if (
        !transaction.dealershipCreateTransaction ||
        transaction.dealershipCreateTransaction.__typename === 'GraphQLError'
      ) {
        toast.error(
          transaction.dealershipCreateTransaction?.message ?? 'Unexpected error'
        );
        return;
      }
      if (!transaction.dealershipCreateTransaction.data.id) {
        toast.error('Got no transaction ID');
        return;
      }

      close({
        at: '/dashboard/transaction/:pId',
        params: {
          pId: transaction.dealershipCreateTransaction.data.id
        }
      });
    }
  });

  return (
    <Modal
      title="New Customer Transaction"
      isOpen
      onClose={close}
      dataTestId="modal-container"
    >
      <div className="flex flex-col space-y-spacing-04">
        <Header totalSteps={2} currentStep={step}></Header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <div className={classNames({ hidden: step !== 1 })}>
            <Step1
              onNext={() => {
                setStep((s) => s + 1);
              }}
              form={form}
            />
          </div>

          <div className={classNames({ hidden: step !== 2 })}>
            <Step2
              back={() => {
                setStep((s) => s - 1);
              }}
              form={form}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
}
